/**
 * Paystack Inline Popup loader.
 *
 * Iter83 introduced the popup so founders keep their builder context, with a
 * full-page redirect to `authorization_url` as the fallback.
 *
 * Iter84 fix: the popup never opened in production. `resumeTransaction` does
 * not exist in inline.js **v1** at all, and in **v2** it lives on an
 * *instance* (`new PaystackPop()`), never as a static on `window.PaystackPop`
 * — so the old static check was false on both versions. Worse, the next
 * branch called `setup({key, reference})`, which cannot resume a
 * server-initialized transaction (it needs email + amount, and starts a new
 * one), and it `return`ed — so whenever PAYSTACK_PUBLIC_KEY was set the
 * redirect fallback was never reached and checkout dead-ended entirely.
 *
 * Now: load v2, resolve `resumeTransaction` from the instance, and treat the
 * hosted-checkout redirect as the only fallback. The loader can no longer
 * hang, so a caller always either gets a popup or leaves for Paystack.
 *
 * Backend contract:
 *   POST /credits/checkout           → {authorization_url, access_code, public_key, reference, ...}
 *   POST /valuations/{id}/review/checkout → same shape (subset)
 *
 * Usage:
 *   import { openPaystackPopup } from "@/lib/paystackInline";
 *   const { data } = await api.post("/credits/checkout", {...});
 *   await openPaystackPopup({
 *     accessCode: data.access_code,
 *     reference:  data.reference,
 *     authorizationUrl: data.authorization_url,  // fallback
 *     onSuccess: () => navigate(`/payment/callback?reference=${data.reference}`),
 *     onClose:   () => toast.info("Checkout cancelled"),
 *   });
 */

const PAYSTACK_INLINE_SRC = "https://js.paystack.co/v2/inline.js";
let _loaderPromise = null;

export function ensurePaystackInline() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.PaystackPop) return Promise.resolve(true);
  if (_loaderPromise) return _loaderPromise;

  _loaderPromise = new Promise((resolve) => {
    const finish = (v) => resolve(v);
    const existing = document.querySelector(`script[src="${PAYSTACK_INLINE_SRC}"]`);
    const target = existing || document.createElement("script");
    if (!existing) {
      target.src = PAYSTACK_INLINE_SRC;
      target.async = true;
    }
    target.addEventListener("load", () => finish(!!window.PaystackPop));
    target.addEventListener("error", () => finish(false));
    if (window.PaystackPop) return finish(true);
    if (!existing) document.head.appendChild(target);
    // Never hang the caller: if the script neither loads nor errors, fall back.
    setTimeout(() => finish(!!window.PaystackPop), 8000);
  });
  return _loaderPromise;
}

/** Resolve v2's instance-based resumeTransaction, tolerating either shape. */
function getResumer() {
  const P = window.PaystackPop;
  if (!P) return null;
  if (typeof P.resumeTransaction === "function") return P.resumeTransaction.bind(P);
  if (typeof P === "function") {
    try {
      const inst = new P();
      if (inst && typeof inst.resumeTransaction === "function") {
        return inst.resumeTransaction.bind(inst);
      }
    } catch (e) { /* fall through to redirect */ }
  }
  return null;
}

export async function openPaystackPopup({
  accessCode,
  // publicKey intentionally unused: setup({key,reference}) cannot resume a
  // server-initialized transaction (needs email+amount) and must never
  // pre-empt the hosted-checkout redirect. Kept out of the destructure.$
  reference,
  authorizationUrl,
  onSuccess,
  onClose,
}) {
  const redirect = () => {
    if (!authorizationUrl) {
      throw new Error("Paystack popup unavailable and no fallback URL provided");
    }
    window.location.href = authorizationUrl;
  };

  const ok = await ensurePaystackInline();
  if (!ok) return redirect();

  if (accessCode) {
    const resume = getResumer();
    if (resume) {
      resume(accessCode, {
        onSuccess: (tx) => { if (onSuccess) onSuccess(tx || { reference }); },
        onCancel:  () => { if (onClose) onClose(); },
        onClose:   () => { if (onClose) onClose(); },
      });
      return;
    }
  }

  // NOTE: PaystackPop.setup({key, reference}) cannot resume a server-initialized
  // transaction (it needs email + amount and would start a new one), so it must
  // never pre-empt the hosted-checkout redirect. Always redirect instead.
  return redirect();
}
